<template>
  <div class="ccgd-card ccgd-card--internal ccgd-card--internal-squish" :data-status="statusKey" @click="onCardClick">
    <div class="ccgd-accent" />

    <div class="ccgd-main">
      <div class="ccgd-header ccgd-header--it">
        <div class="ccgd-title-row ccgd-title-row--it">
          <div class="ccgd-title">
            {{ safeTitle(item) || "Internal Task" }}
          </div>

          <div class="ccgd-right-pack">
            <div class="ccgd-meta-inline" v-if="assignedTo">
              <i class="fa-duotone fa-user-tie ccgd-ico ccgd-ico--assignee"></i>
              <span class="ccgd-meta-text">{{ assignedTo }}</span>
            </div>

            <div class="ccgd-status-pill" :data-status="statusKey">
              <span class="ccgd-status-dot"></span>
              {{ statusText }}
            </div>
          </div>
        </div>
      </div>

      <div class="ccgd-compact-row">
        <div class="ccgd-compact-left">
          <div class="ccgd-compact-chip">
            <i class="fa-duotone fa-calendar ccgd-ico ccgd-ico--created"></i>
            <span class="ccgd-compact-label">Created</span>
            <span class="ccgd-compact-value">{{ createdDate }}</span>
          </div>

          <div class="ccgd-compact-chip">
            <i class="fa-duotone fa-pen-to-square ccgd-ico ccgd-ico--updated"></i>
            <span class="ccgd-compact-label">Updated</span>
            <span class="ccgd-compact-value">{{ lastUpdatedLabel }}</span>
          </div>

          <div class="ccgd-compact-chip" v-if="followUpDate">
            <i class="fa-duotone fa-calendar-days ccgd-ico ccgd-ico--followup"></i>
            <span class="ccgd-compact-label">Follow Up</span>
            <span class="ccgd-compact-value">{{ followUpDate }}</span>
          </div>
        </div>

        <div class="ccgd-compact-right" @click.stop>
          <div class="ccgd-actions-inline">
            <kbutton
              v-if="!item.completed"
              class="ccgd-btn ccgd-btn--primary ccgd-btn--sm"
              @click.native.stop="$emit('complete', item)"
              title="Complete Task"
            >
              <i class="fa-duotone fa-check ccgd-ico ccgd-ico--action"></i>
              Click Here to Mark Completed
            </kbutton>

            <span v-else class="ccgd-pill ccgd-pill--complete ccgd-pill--sm">
              <i class="fa-duotone fa-circle-check ccgd-ico ccgd-ico--action"></i>
              Completed
            </span>

            <slot name="actionsCell" />
          </div>
        </div>
      </div>

      <div v-if="hasNotes" class="ccgd-notes-row">
        <div class="ccgd-desc-inline ccgd-desc-inline--sm">
          <i class="fa-duotone fa-note-sticky ccgd-ico ccgd-ico--note"></i>
          <span class="ccgd-notes-block">{{ notesText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button } from "@progress/kendo-vue-buttons"

export default {
  name: "InternalTaskCard",
  components: { kbutton: Button },
  props: { item: { type: Object, required: true } },
  computed: {
    assignedTo() {
      const v = this.item && this.item.assignedto
      if (v == null) return null
      const s = String(v).trim()
      if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return null
      return s
    },
    statusText() {
      if (this.item.completed) return "Completed"
      return this.item.status || "Open"
    },
    statusKey() {
      return (this.item.completed ? "completed" : (this.item.status || "open")).toLowerCase()
    },
    createdDate() {
      return this.formatDate(this.item.created)
    },
    followUpDate() {
      return this.item.followupdate ? this.formatDate(this.item.followupdate) : null
    },
    lastUpdatedLabel() {
      const v = this.item.lastupdated
      if (v == null) return "—"
      const s = String(v).trim()
      return s ? s : "—"
    },
    notesText() {
      const n = this.item && (this.item.notes || this.item.description)
      if (n == null) return "N/A"
      const s = String(n).trim()
      return s ? s : "N/A"
    },
    hasNotes() {
      return this.notesText !== "N/A"
    }
  },
  methods: {
    safeTitle(item) {
      if (!item) return ""
      const t = item.title || item.name || ""
      if (typeof t !== "string") return ""
      const looksLikeAge = /^\s*\d+\s*(day|days)\s*$/i.test(t)
      if (looksLikeAge) return item.taskname || item.tasktitle || "Task"
      return t
    },
    normalizeUrl(url) {
      if (!url) return null
      const s = String(url).trim()
      if (!s) return null
      if (s.startsWith("http://") || s.startsWith("https://")) return s
      return `https://${s}`
    },
    openLink(url) {
      const safe = this.normalizeUrl(url)
      if (!safe) return
      setTimeout(() => {
        window.open(safe, "_blank", "noopener,noreferrer")
      }, 0)
    },
    onCardClick() {
      if (!this.item || !this.item.tasklink) return
      this.openLink(this.item.tasklink)
    },
    formatDate(val) {
      if (!val) return "—"
      const d = new Date(val)
      if (isNaN(d.getTime())) return String(val)
      return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })
    }
  }
}
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-title-row--it {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 950;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccgd-right-pack {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.ccgd-meta-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.72);
  max-width: 240px;
  min-width: 0;
}

.ccgd-meta-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-status-pill {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.85);
  white-space: nowrap;
}

.ccgd-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
}

.ccgd-compact-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.ccgd-compact-left {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.ccgd-compact-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 11.5px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.75);
  flex: 0 0 auto;
  white-space: nowrap;
}

.ccgd-compact-label {
  font-size: 10.5px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-compact-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.88);
}

.ccgd-compact-right {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}

.ccgd-actions-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-top: 0;
}

.ccgd-notes-row {
  margin-top: 6px;
}

.ccgd-desc-inline {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12.5px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.72);
  width: 100%;
  max-width: 100%;
}

.ccgd-desc-inline--sm {
  padding: 7px 9px;
  font-size: 12px;
  border-radius: 11px;
}

.ccgd-notes-block {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ccgd-btn--sm {
  padding: 6px 10px !important;
  font-size: 12px !important;
  border-radius: 10px !important;
}

.ccgd-pill--sm {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}

.ccgd-ico--assignee {
  color: #16a34a;
}

.ccgd-ico--followup {
  color: #f59e0b;
}

.ccgd-ico--updated,
.ccgd-ico--created,
.ccgd-ico--note {
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}

@media (max-width: 780px) {
  .ccgd-compact-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ccgd-compact-left {
    flex-wrap: wrap;
  }
}
</style>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-title-row--it {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 950;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccgd-right-pack {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ccgd-meta-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.72);
  max-width: 260px;
  min-width: 0;
}

.ccgd-meta-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-status-pill {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.85);
  white-space: nowrap;
}

.ccgd-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
}

.ccgd-compact-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.ccgd-compact-left {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.ccgd-compact-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.75);
  flex: 0 0 auto;
  white-space: nowrap;
}

.ccgd-compact-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-compact-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.88);
}

.ccgd-compact-right {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}

.ccgd-actions-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-top: 2px;
}

.ccgd-notes-row {
  margin-top: 8px;
}

.ccgd-desc-inline {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12.5px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.72);
  width: 100%;
  max-width: 100%;
}

.ccgd-notes-block {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}

.ccgd-ico--assignee {
  color: #16a34a;
}

.ccgd-ico--followup {
  color: #f59e0b;
}

.ccgd-ico--updated,
.ccgd-ico--created,
.ccgd-ico--note {
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}

@media (max-width: 780px) {
  .ccgd-compact-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ccgd-compact-left {
    flex-wrap: wrap;
  }
}
</style>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-header--it,
.ccgd-title-wrap,
.ccgd-title-row--it {
  width: 100%;
}

.ccgd-title-row--it {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 950;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccgd-status-pill {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.85);
  white-space: nowrap;
}

.ccgd-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
}

.ccgd-submeta-row--it {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.ccgd-meta-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.72);
  max-width: 100%;
  min-width: 0;
}

.ccgd-meta-text {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-info-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ccgd-info-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.75);
  max-width: 100%;
}

.ccgd-info-chip-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-info-chip-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.88);
  white-space: nowrap;
}

.ccgd-bottom-row {
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.ccgd-bottom-left {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.ccgd-bottom-right {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

.ccgd-actions-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-top: 2px;
}

.ccgd-desc-inline {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12.5px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.72);
  width: 100%;
  max-width: 100%;
  margin-right: 14px;
}

.ccgd-desc-inline.is-empty {
  opacity: 0.75;
}

.ccgd-desc-inline span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}


.ccgd-ico--assignee { color: #16a34a; }


.ccgd-ico--followup { color: #f59e0b; }

.ccgd-ico--updated,
.ccgd-ico--created,
.ccgd-ico--note,
.ccgd-ico--user,
.ccgd-ico--loan {
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}

@media (max-width: 780px) {
  .ccgd-bottom-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ccgd-actions-inline {
    margin-top: 8px;
  }

  .ccgd-desc-inline {
    margin-right: 0;
  }

  .ccgd-bottom-right {
    justify-content: flex-start;
  }
}
</style>
